// ListPage (page patterns, BL-189): a page whose answer is a list (Customers › Equipment, Stock › Catalog
// and prices, Inbox › Notifications, Tasks, Jobs to review). Top to bottom: the title block (PageHead);
// the views of the list as words, the chosen one underlined in ink, with any other filter beside them;
// then the list, about 60 % of the width, and what goes with it on the right (the services due, what
// the agents did), divided by a hairline; one column under 900, the side after the list.
// Each region is a section: an ink rule above, its name and count under it, then its rows. A region
// says its own state, so every list page says it the same way: loading draws skeleton rows, empty one
// muted sentence, failed one red sentence and Try again. No region waits forever: still loading after
// 10 s it says so under the rows ("Still loading the equipment…"), and after 30 s it gives up into the
// red sentence with Try again ("Couldn’t load the equipment."), which starts the wait again. The rows themselves are a catalogued list
// (EquipmentList, JobTable, TaskRow, RecordRow…); the pattern never draws a row.
// A list found by typing (All customers) has its search first in the filters' row, as wide as a sentence,
// the views' words lined up with the box's foot.
// A rail on the left (the price book's TreeList, DEC-019) narrows the list to where things sit; the
// views and filters then sit over the list beside it, and under 900 the rail goes above.
// Not for a record (RecordPage), a page of settings (SettingsPage) or the board. Reads only
// --listpage-*; PageHead, Field, Link and Skeleton read their own.
import { createContext, useContext, useEffect, useId, useState, type ReactNode } from 'react';
import { TextField } from '../../components/Field/Field.tsx';
import { Link } from '../../components/Link/Link.tsx';
import { Skeleton, SkeletonGroup } from '../../components/Skeleton/Skeleton.tsx';
import { PageHead, type PageHeadProps } from '../PageHead/PageHead.tsx';
import './ListPage.css';

export type ListStatus = 'ready' | 'loading' | 'empty' | 'failed' | 'slow' | 'timedout';

/** A region still loading says so after this long (RecordPage's slow), */
export const LIST_SLOW_MS = 10_000;
/** and gives up after this long, into Couldn’t load and Try again. */
export const LIST_GIVE_UP_MS = 30_000;

const WaitTimes = createContext({ slowAfter: LIST_SLOW_MS, giveUpAfter: LIST_GIVE_UP_MS });

/** Shorter waits for every ListPage region inside (a page story's Slow and Timed out, a test). */
export function ListWaitTimes({
  slowAfter = LIST_SLOW_MS,
  giveUpAfter = LIST_GIVE_UP_MS,
  children,
}: {
  slowAfter?: number;
  giveUpAfter?: number;
  children: ReactNode;
}) {
  return <WaitTimes.Provider value={{ slowAfter, giveUpAfter }}>{children}</WaitTimes.Provider>;
}

/** What a region that is still loading says, `ms` into its wait. */
export function listWaitAt(
  ms: number,
  slowAfter = LIST_SLOW_MS,
  giveUpAfter = LIST_GIVE_UP_MS,
): 'loading' | 'slow' | 'timedout' {
  return ms >= giveUpAfter ? 'timedout' : ms >= slowAfter ? 'slow' : 'loading';
}

/** A region of the page: the list, or what goes with it. */
export interface ListRegion {
  /** Its name, under the rule ("Everything", "Service due soon"). */
  title: string;
  /** How many there are, beside the name; none while loading. */
  count?: number;
  /** Its rows, when it's ready. */
  children?: ReactNode;
  /** Over it in its column, whatever its state: what the page shows first there, a Section or a part
   * (the map's visit picked, over Where everyone is). */
  before?: ReactNode;
  /** Under it in its column, whatever its state: a part of its own that goes with it, a Section (Time
   * off's Company holidays under Who's off). */
  after?: ReactNode;
  /** Ready, loading, empty or failed; the pattern turns a long loading into slow, then timed out.
   * Slow and timed out can be given too (a story, a page that knows). */
  status?: ListStatus;
  /** What it lists, for the words the pattern says ("the equipment", "what’s due"); its title by default. */
  what?: string;
  /** Empty: what isn't there, in a sentence ("No service is due in the next 30 days."). */
  emptyText?: string;
  /** Failed: what didn't load ("The equipment didn't load."). */
  failedText?: string;
  onRetry?: () => void;
  /** Loading: what's coming, for screen readers ("Loading the equipment"). */
  loadingText?: string;
  /** How many skeleton rows to draw while loading (3). */
  loadingRows?: number;
  /** How long a load waits before it says it's slow, and before it gives up (10 s and 30 s, or
   * ListWaitTimes'). */
  slowAfter?: number;
  giveUpAfter?: number;
}

/** A loading region's wait: loading, then slow, then timed out; again starts it over (Try again). */
function useWait(on: boolean, slowAfter: number, giveUpAfter: number) {
  const first = () => listWaitAt(0, slowAfter, giveUpAfter);
  const [wait, setWait] = useState(first);
  const [round, setRound] = useState(0);
  // a new load (loading again after an answer) starts its wait over
  const [wasOn, setWasOn] = useState(on);
  if (on !== wasOn) {
    setWasOn(on);
    setWait(first());
  }
  useEffect(() => {
    if (!on) return;
    const t = [slowAfter, giveUpAfter].map((ms) =>
      window.setTimeout(() => setWait(listWaitAt(ms, slowAfter, giveUpAfter)), ms),
    );
    return () => t.forEach((x) => window.clearTimeout(x));
  }, [on, round, slowAfter, giveUpAfter]);
  const again = () => {
    setWait(first());
    setRound((n) => n + 1);
  };
  return { wait, again };
}

/**
 * A loading region's wait, for a pattern whose parts say their own state the way ListPage's do
 * (DashboardPage's sections): loading, then slow, then timed out, ListWaitTimes' waits unless given;
 * `again` (Try again) starts it over.
 */
export function useListWait(on: boolean, slowAfter?: number, giveUpAfter?: number) {
  const times = useContext(WaitTimes);
  return useWait(on, slowAfter ?? times.slowAfter, giveUpAfter ?? times.giveUpAfter);
}

/** One view of the list (Everything, Service due, Warranty ending). */
export interface ListView {
  id: string;
  label: string;
}

/** A filter beside the views as the list pages draw it: its name, muted, and the browser's own select
 * on a hairline ("Kind: Every kind"). For a search or anything else, pass a catalogued field. */
export function ListPick({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: readonly { value: string; label: string }[];
  onChange: (value: string) => void;
}) {
  return (
    <label className="fl-listpage__pick">
      <span>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/** A search before the views: its label above the box, about a sentence wide (Find a customer). */
export interface ListSearch {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  /** The most it takes, as the list's action allows (100 for a number or a name). */
  maxLength?: number;
}

export interface ListPageProps {
  head: PageHeadProps;
  /** Lines under the head, before the list, the whole width: what was just done (Booked for…), why the
   * page can't change it now. The page draws them. */
  notice?: ReactNode;
  /** The list's search, first in the filters' row (All customers' Find a customer). */
  search?: ListSearch;
  /** The views, as words; the chosen one underlined. `label` names the group ("Show"). */
  views?: { label: string; items: readonly ListView[]; value: string; onChange: (id: string) => void };
  /** Any other filter, after the views: a ListPick (Kind), a search. */
  filters?: ReactNode;
  list: ListRegion;
  /** What goes with the list, on the right. */
  aside?: ListRegion;
  /** Under the list, the whole width: something folded that belongs to the page (Possible duplicates). */
  below?: ReactNode;
  /** Over the page: a FormSheet, a SidePanel, a dialog. */
  layers?: ReactNode;
  /** On the left, beside the filters and the list: what narrows it (a TreeList). */
  rail?: ReactNode;
  /** In place of the filters and the list: one record at a time (a ReviewQueue). */
  replace?: ReactNode;
}

function Region({ r, id }: { r: ListRegion; id: string }) {
  const given = r.status ?? 'ready';
  const { wait, again } = useListWait(given === 'loading', r.slowAfter, r.giveUpAfter);
  const status = given === 'loading' ? wait : given;
  const what = r.what ?? r.title.toLowerCase();
  const retry = () => {
    again();
    r.onRetry?.();
  };
  const rows = (label: string) => (
    <SkeletonGroup label={label}>
      {Array.from({ length: r.loadingRows ?? 3 }, (_, i) => (
        <Skeleton key={i} shape="row" width="full" />
      ))}
    </SkeletonGroup>
  );
  return (
    <section className="fl-listpage__sec" aria-labelledby={id}>
      <h2 className="fl-listpage__h" id={id}>
        {r.title}
        {status === 'ready' && r.count != null ? <span className="fl-listpage__cnt">{r.count}</span> : null}
      </h2>
      {status === 'loading' ? (
        rows(r.loadingText ?? `Loading ${what}`)
      ) : status === 'slow' ? (
        <>
          {rows(`Still loading ${what}`)}
          <p className="fl-listpage__say" data-slow="" role="status">
            {`Still loading ${what}…`}
          </p>
        </>
      ) : status === 'timedout' ? (
        <p className="fl-listpage__say" data-bad="" role="alert">
          <span>{`Couldn’t load ${what}.`}</span>
          <Link onPress={retry}>Try again</Link>
        </p>
      ) : status === 'empty' ? (
        <p className="fl-listpage__say">{r.emptyText ?? 'Nothing here yet.'}</p>
      ) : status === 'failed' ? (
        <p className="fl-listpage__say" data-bad="" role="alert">
          <span>{r.failedText ?? 'This didn’t load.'}</span>
          {r.onRetry ? <Link onPress={r.onRetry}>Try again</Link> : null}
        </p>
      ) : (
        r.children
      )}
    </section>
  );
}

export function ListPage({
  head,
  notice,
  search,
  views,
  filters,
  list,
  aside,
  below,
  layers,
  rail,
  replace,
}: ListPageProps) {
  const id = useId();
  const body = replace ? (
    <div className="fl-listpage__replace">{replace}</div>
  ) : (
    <Body id={id} search={search} views={views} filters={filters} list={list} aside={aside} below={below} />
  );
  return (
    <div className="fl-listpage" data-figures={head.figures ? '' : undefined}>
      <PageHead {...head} />
      {notice ? <div className="fl-listpage__notice">{notice}</div> : null}
      {rail && !replace ? (
        <div className="fl-listpage__railed">
          <div className="fl-listpage__rail">{rail}</div>
          <div className="fl-listpage__main">{body}</div>
        </div>
      ) : (
        body
      )}
      {layers}
    </div>
  );
}

function Body({
  id,
  search,
  views,
  filters,
  list,
  aside,
  below,
}: Pick<ListPageProps, 'search' | 'views' | 'filters' | 'list' | 'aside' | 'below'> & { id: string }) {
  return (
    <>
      {search || views || filters ? (
        <div className="fl-listpage__filters" data-search={search ? '' : undefined}>
          {search ? (
            <div className="fl-listpage__search">
              <TextField
                type="search"
                label={search.label}
                value={search.value}
                onChange={search.onChange}
                {...(search.placeholder ? { placeholder: search.placeholder } : {})}
                {...(search.autoFocus ? { autoFocus: true } : {})}
                {...(search.maxLength != null ? { maxLength: search.maxLength } : {})}
              />
            </div>
          ) : null}
          {views ? (
            <div className="fl-listpage__views" role="group" aria-label={views.label}>
              {views.items.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  className="fl-listpage__view"
                  aria-pressed={views.value === v.id}
                  onClick={() => views.onChange(v.id)}
                >
                  {v.label}
                </button>
              ))}
            </div>
          ) : null}
          {filters}
        </div>
      ) : null}
      <div className="fl-listpage__cols" data-aside={aside ? '' : undefined}>
        <div className="fl-listpage__col">
          {list.before}
          <Region r={list} id={`${id}-list`} />
          {list.after}
        </div>
        {aside ? (
          <div className="fl-listpage__col">
            {aside.before}
            <Region r={aside} id={`${id}-aside`} />
            {aside.after}
          </div>
        ) : null}
      </div>
      {below ? <div className="fl-listpage__below">{below}</div> : null}
    </>
  );
}
